import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, Moon, Sun, LogOut, User, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useThemeStore } from '@/stores/themeStore'
import { Button } from '@/components/ui/button'
import { useState, useRef } from 'react'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'


export function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useThemeStore()
  const [showAbout, setShowAbout] = useState(false)
  const [isHolding, setIsHolding] = useState(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const forceUpdate = async () => {
    toast('Tommer cache og oppdaterer...', { duration: 2000 })
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(registrations.map(r => r.unregister()))
      }
      const keys = await caches.keys()
      await Promise.all(keys.map(key => caches.delete(key)))
      setTimeout(() => window.location.reload(), 500)
    } catch {
      window.location.reload()
    }
  }

  const handleHoldStart = () => {
    setIsHolding(true)
    holdTimer.current = setTimeout(() => forceUpdate(), 3000)
  }

  const handleHoldEnd = () => {
    setIsHolding(false)
    if (holdTimer.current) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
  }

  // Show PCK Pocket on /sok, Grønvold logo everywhere else
  const isPckPocket = location.pathname === '/sok'

  return (
    <div className="h-full bg-background flex flex-col overflow-hidden">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center">
          {/* Left - Hamburger menu */}
          <div className="flex-1 flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? (
                    <Sun className="mr-2 h-4 w-4" />
                  ) : (
                    <Moon className="mr-2 h-4 w-4" />
                  )}
                  {theme === 'dark' ? 'Lyst tema' : 'Morkt tema'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Center - Logo/Home */}
          <button
            onClick={() => navigate('/', { replace: true })}
            className="text-xl font-black tracking-tight text-foreground uppercase hover:text-primary transition-colors"
          >
            {isPckPocket ? 'PCK Pocket' : 'GRØNVOLD'}
          </button>

          {/* Right - User menu */}
          <div className="flex-1 flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">Bruker</span>
                    <span className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Logg ut
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col overflow-hidden">
        <Outlet />
      </main>

      <footer className="py-4 text-center text-xs text-muted-foreground">
        <button
          onClick={() => setShowAbout(true)}
          className="hover:text-foreground transition-colors"
        >
          Powered by Tomatic | v{__APP_VERSION__.split('.').slice(0, 2).join('.')}
        </button>
      </footer>

      {/* About modal */}
      <AnimatePresence>
        {showAbout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center p-4"
            onClick={() => setShowAbout(false)}
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`bg-background border border-border rounded-t-xl w-full max-w-sm p-6 text-center relative select-none transition-transform ${isHolding ? 'scale-[0.98]' : ''}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleHoldStart}
              onMouseUp={handleHoldEnd}
              onMouseLeave={handleHoldEnd}
              onTouchStart={handleHoldStart}
              onTouchEnd={handleHoldEnd}
            >
              <button
                onClick={() => setShowAbout(false)}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-4 h-4" />
              </button>

              <h2 className="text-2xl font-black tracking-tight text-foreground uppercase mb-3">
                TOMATIC<sup className="text-xs font-normal">™</sup>
              </h2>

              <p className="text-sm text-muted-foreground mb-4">
                {__APP_VERSION__} · Build {__BUILD_NUMBER__}
              </p>

              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>Built with TOMATIC Engine</p>
                <p>Logistics Automation Platform</p>
              </div>

              {isHolding && (
                <p className="text-xs text-primary mt-4 animate-pulse">
                  Hold for a oppdatere...
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
